import React, { useState, useEffect } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  ScrollView, 
  TouchableOpacity, 
  Alert, 
  ActivityIndicator,
  Image,
  ViewStyle
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit 
} from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { Exercise } from '../../types';
import { useTheme } from '../../context/ThemeContext';
import { MUSCLE_GROUPS } from '../../constants/muscleGroups';

type Props = NativeStackScreenProps<MainStackParamList, 'ExerciseLibraryDetail'>;

interface ProgressRecord {
  id: string;
  weight: number;
  series: number;
  reps: number;
  date: any;
  routineId: string;
  routineName?: string;
}

const ExerciseLibraryDetailScreen = ({ route, navigation }: Props) => {
  const { exerciseId } = route.params;
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [progressHistory, setProgressHistory] = useState<ProgressRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [personalBest, setPersonalBest] = useState<{weight: number, date: Date} | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    fetchExerciseDetails();
    fetchProgressHistory();
  }, [exerciseId]);

  const fetchExerciseDetails = async () => {
    try {
      const exerciseRef = doc(firestore, 'exercises', exerciseId);
      const exerciseDoc = await getDoc(exerciseRef);
      
      if (!exerciseDoc.exists()) {
        Alert.alert('Error', 'No se encontró el ejercicio');
        navigation.goBack();
        return;
      }
      
      setExercise({
        id: exerciseDoc.id,
        ...exerciseDoc.data()
      } as Exercise);
    } catch (error) {
      console.error('Error al obtener detalles del ejercicio:', error);
      Alert.alert('Error', 'No se pudieron cargar los detalles del ejercicio');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressHistory = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      const progressRef = collection(firestore, 'progress');
      const q = query(
        progressRef,
        where('userId', '==', userId),
        where('exerciseId', '==', exerciseId),
        orderBy('date', 'desc'),
        limit(10)
      );
      
      const querySnapshot = await getDocs(q);
      const history: ProgressRecord[] = [];
      let maxWeight = 0;
      let maxWeightDate: Date | null = null;
      
      // Obtener IDs de rutinas para hacer un segundo query
      const routineIds = new Set<string>();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        routineIds.add(data.routineId);
        
        // Track personal best
        if (data.weight > maxWeight) {
          maxWeight = data.weight;
          maxWeightDate = data.date.toDate();
        }
        
        history.push({
          id: doc.id,
          ...data
        } as ProgressRecord);
      });
      
      // Obtener nombres de rutinas si hay registros
      if (routineIds.size > 0) {
        const routinesData: {[key: string]: string} = {};
        
        await Promise.all(
          Array.from(routineIds).map(async (routineId) => {
            const routineDoc = await getDoc(doc(firestore, 'routines', routineId));
            if (routineDoc.exists()) {
              routinesData[routineId] = routineDoc.data().name;
            }
          })
        );
        
        // Añadir nombres de rutinas a los registros
        history.forEach(record => {
          record.routineName = routinesData[record.routineId] || 'Rutina desconocida';
        });
      }
      
      setProgressHistory(history);
      
      if (maxWeight > 0 && maxWeightDate) {
        setPersonalBest({
          weight: maxWeight,
          date: maxWeightDate
        });
      }
    } catch (error) {
      console.error('Error al obtener historial de progreso:', error);
      Alert.alert('Error', 'No se pudo cargar el historial de progreso');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Función para obtener el nombre del grupo muscular a partir del ID
  const getMuscleGroupName = (muscleGroupId: string): string => {
    const muscleGroup = MUSCLE_GROUPS.find(group => group.id === muscleGroupId);
    return muscleGroup ? muscleGroup.name : muscleGroupId;
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (!exercise) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorText, { color: theme.colors.error }]}>No se pudo cargar el ejercicio</Text>
        <Button 
          title="Volver" 
          onPress={() => navigation.goBack()} 
          type="primary" 
        />
      </View>
    );
  }

  const formattedDate = (date: any) => {
    const d = date.toDate();
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  };

  return (
    <ScrollView style={[styles.scrollView, { backgroundColor: theme.colors.background }]}>
      <View style={styles.container}>
        {/* Exercise Header */}
        <View style={styles.exerciseHeader}>
          <Text style={[styles.exerciseTitle, { color: theme.colors.text }]}>{exercise.name}</Text>
          
          <View style={styles.muscleGroups}>
            {exercise.muscleGroups.map((muscleId, index) => (
              <View key={index} style={[styles.muscleTag, { backgroundColor: theme.colors.primary }]}>
                <Text style={[styles.muscleText, { color: theme.colors.white }]}>
                  {getMuscleGroupName(muscleId)}
                </Text>
              </View>
            ))}
          </View>
          
          <Text style={[styles.difficultyText, { color: theme.colors.grey.dark }]}>
            Nivel: {
              exercise.difficultyLevel === 'beginner' ? 'Principiante' :
              exercise.difficultyLevel === 'intermediate' ? 'Intermedio' : 'Avanzado'
            }
          </Text>
        </View>
        
        {/* Image if available */}
        {exercise.imageUrl && (
          <Card style={styles.imageCard}>
            <Image 
              source={{ uri: exercise.imageUrl }} 
              style={styles.exerciseImage}
              resizeMode="cover"
            />
          </Card>
        )}
        
        {/* Personal Best */}
        {personalBest && (
          <Card style={{
            ...styles.personalBestCard,
            backgroundColor: theme.colors.background,
            borderColor: theme.colors.warning
          }}>
            <View style={styles.personalBestContent}>
              <View style={styles.personalBestIconContainer}>
                <Ionicons name="trophy" size={24} color={theme.colors.warning} />
              </View>
              <View>
                <Text style={[styles.personalBestLabel, { color: theme.colors.grey.dark }]}>Tu Mejor Marca</Text>
                <Text style={[styles.personalBestValue, { color: theme.colors.text }]}>{personalBest.weight} kg</Text>
                <Text style={[styles.personalBestDate, { color: theme.colors.grey.dark }]}>
                  {personalBest.date.getDate()}/{personalBest.date.getMonth() + 1}/{personalBest.date.getFullYear()}
                </Text>
              </View>
            </View>
          </Card>
        )}
        
        {/* Exercise Description */}
        <Card style={styles.card}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>Descripción</Text>
          <Text style={[styles.cardText, { color: theme.colors.text }]}>{exercise.description}</Text>
        </Card>
        
        {/* Exercise Instructions */}
        <Card style={styles.card}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>Instrucciones</Text>
          <Text style={[styles.cardText, { color: theme.colors.text }]}>{exercise.instructions}</Text>
        </Card>
        
        {/* Progress History */}
        <Card style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={[styles.cardTitle, { color: theme.colors.text }]}>Historial de Progreso</Text>
            <TouchableOpacity
              onPress={() => navigation.navigate('WeightTracking', { exerciseId })}
            >
              <Text style={[styles.viewAllText, { color: theme.colors.primary }]}>Ver Todos</Text>
            </TouchableOpacity>
          </View>
          
          {historyLoading ? (
            <ActivityIndicator size="small" color={theme.colors.primary} />
          ) : progressHistory.length > 0 ? (
            progressHistory.map((record, index) => (
              <View key={record.id || index} style={[styles.historyItem, { borderBottomColor: theme.colors.grey.light }]}>
                <View style={styles.historyDate}>
                  <Text style={[styles.dateText, { color: theme.colors.text }]}>{formattedDate(record.date)}</Text>
                  <Text style={[styles.routineText, { color: theme.colors.grey.dark }]}>{record.routineName}</Text>
                </View>
                
                <View style={styles.historyDetails}>
                  <View style={styles.historyDetail}>
                    <Text style={[styles.historyValue, { color: theme.colors.primary }]}>{record.weight} kg</Text>
                    <Text style={[styles.historyLabel, { color: theme.colors.grey.dark }]}>Peso</Text>
                  </View>
                  
                  <View style={styles.historyDetail}>
                    <Text style={[styles.historyValue, { color: theme.colors.primary }]}>{record.series}</Text>
                    <Text style={[styles.historyLabel, { color: theme.colors.grey.dark }]}>Series</Text>
                  </View>
                  
                  <View style={styles.historyDetail}>
                    <Text style={[styles.historyValue, { color: theme.colors.primary }]}>{record.reps}</Text>
                    <Text style={[styles.historyLabel, { color: theme.colors.grey.dark }]}>Reps</Text>
                  </View>
                </View>
              </View>
            ))
          ) : (
            <Text style={[styles.emptyText, { color: theme.colors.grey.dark }]}>No hay registros de progreso</Text>
          )}
        </Card>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  container: {
    padding: spacing.lg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  errorText: {
    fontSize: typography.fontSize.lg,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  exerciseHeader: {
    marginBottom: spacing.lg,
  },
  exerciseTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: 'bold',
    marginBottom: spacing.sm,
  },
  muscleGroups: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: spacing.sm,
  },
  muscleTag: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
    marginRight: spacing.xs,
    marginBottom: spacing.xs,
  },
  muscleText: {
    fontSize: typography.fontSize.sm,
  },
  difficultyText: {
    fontSize: typography.fontSize.md,
  },
  card: {
    marginBottom: spacing.lg,
  },
  imageCard: {
    marginBottom: spacing.lg,
    padding: 0,
    overflow: 'hidden',
  },
  exerciseImage: {
    width: '100%',
    height: 200,
    borderRadius: borderRadius.md,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.md,
  },
  cardText: {
    fontSize: typography.fontSize.md,
    lineHeight: 24,
  },
  viewAllText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
  },
  historyItem: {
    borderBottomWidth: 1,
    paddingVertical: spacing.md,
  },
  historyDate: {
    marginBottom: spacing.sm,
  },
  dateText: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  routineText: {
    fontSize: typography.fontSize.sm,
  },
  historyDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  historyDetail: {
    alignItems: 'center',
  },
  historyValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  historyLabel: {
    fontSize: typography.fontSize.xs,
    marginTop: 2,
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    paddingVertical: spacing.md,
  },
  personalBestCard: {
    borderWidth: 2,
    marginBottom: spacing.lg,
  },
  personalBestContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  personalBestIconContainer: {
    marginRight: spacing.md,
  },
  personalBestLabel: {
    fontSize: typography.fontSize.sm,
  },
  personalBestValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
  },
  personalBestDate: {
    fontSize: typography.fontSize.xs,
  },
});

export default ExerciseLibraryDetailScreen; 